import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Edit,
  Trash2,
  Eye,
  Search,
  Filter
} from 'lucide-react';
import { useNotify } from 'react-admin';
import { deleteNcrTemplate, getAllNcrTemplates, type NcrTemplate } from '../../../services/ncrTemplateService.ts';
import './NcrTemplateList.css';

const NcrTemplateList: React.FC = () => {
  const navigate = useNavigate();
  const notify = useNotify();
  const [templates, setTemplates] = useState<NcrTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState('');
  const [filterActive, setFilterActive] = useState<'all' | 'active' | 'inactive'>('all');

  const fetchTemplates = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getAllNcrTemplates();
      setTemplates(data);
    } catch (error) {
      notify('Failed to load templates', { type: 'warning' });
      console.error('Error fetching templates:', error);
    } finally {
      setLoading(false);
    }
  }, [notify]);

  useEffect(() => {
    fetchTemplates();
  }, [fetchTemplates]);

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this template?')) {
      try {
        await deleteNcrTemplate(id);
        notify('Template deleted successfully');
        fetchTemplates();
      } catch (error) {
        notify('Failed to delete template', { type: 'warning' });
        console.error('Error deleting template:', error);
      }
    }
  };

  const filteredTemplates = templates.filter(template => {
    const matchesSearch = template.name.toLowerCase().includes(searchText.toLowerCase()) ||
      (template.description?.toLowerCase().includes(searchText.toLowerCase()) || false);
    const matchesFilter = filterActive === 'all' || 
      (filterActive === 'active' && template.isActive) ||
      (filterActive === 'inactive' && !template.isActive);
    return matchesSearch && matchesFilter;
  });

  if (loading) {
    return <div className="ncr-loading">Loading...</div>;
  }

  return (
    <div className="ncr-template-list-container">
      <div className="ncr-header">
        <h1>NCR Template Management</h1>
        <button
          className="ncr-btn ncr-btn-primary"
          onClick={() => navigate('/NcrTemplate/create')}
        >
          <Plus size={20} />
          Create Template
        </button>
      </div>

      <div className="ncr-toolbar">
        <div className="ncr-search-box">
          <Search size={18} />
          <input
            type="text"
            placeholder="Search template name or description..."
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
        </div>

        <div className="ncr-filter-box">
          <Filter size={18} />
          <select
            value={filterActive}
            onChange={(e) => setFilterActive(e.target.value as any)}
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>
        </div>
      </div>

      <div className="ncr-table-container">
        <table className="ncr-table">
          <thead>
            <tr>
              <th>Name</th>
              <th>Description</th>
              <th>Stages</th>
              <th>Properties</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredTemplates.length === 0 ? (
              <tr>
                <td colSpan={7} className="ncr-empty-row">
                  No templates found
                </td>
              </tr>
            ) : (
              filteredTemplates.map((template) => (
                <tr key={template.id}>
                  <td className="ncr-name">{template.name}</td>
                  <td>{template.description || '-'}</td>
                  <td className="ncr-center">
                    <span className="ncr-badge">{template.stages?.length || 0}</span>
                  </td>
                  <td className="ncr-center">
                    <span className="ncr-badge">{template.properties?.length || 0}</span>
                  </td>
                  <td className="ncr-center">
                    <span className={`ncr-status ${template.isActive ? 'active' : 'inactive'}`}>
                      {template.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>{template.createdAt ? new Date(template.createdAt).toLocaleDateString() : '-'}</td>
                  <td className="ncr-actions">
                    <button
                      className="ncr-btn-icon ncr-btn-view"
                      onClick={() => navigate(`/NcrTemplate/${template.id}/view`)}
                      title="View"
                    >
                      <Eye size={18} />
                    </button>
                    <button
                      className="ncr-btn-icon ncr-btn-edit"
                      onClick={() => navigate(`/NcrTemplate/${template.id}/edit`)}
                      title="Edit"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      className="ncr-btn-icon ncr-btn-delete"
                      onClick={() => template.id != null && handleDelete(template.id)}
                      title="Delete"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default NcrTemplateList;
